import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import TaskTemplateFavoriteHeart from '@/components/Tasks/TaskTemplateFavoriteHeart';
import { scheduleLabel, isNewTemplate } from '@/utils/tasks_catalog';

// Template catalog cards share the neutral surfaces used across the library.
function TemplatePinCard({ template, onPress, onSchedulePress, width, stretchHeight = false }) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isMD } = useResponsiveness();
  const palette = getCatalogPalette(theme);

  const isNew = isNewTemplate(template);

  const styles = useMemo(
    () => createStyles(colors, palette, isXS, isMD, stretchHeight),
    [colors, palette, isXS, isMD, stretchHeight]
  );

  const scheduleHandler = onSchedulePress ?? onPress;

  return (
    <Pressable
      onPress={() => onPress?.(template)}
      accessibilityRole="link"
      accessibilityLabel={template?.name}
      style={({ hovered, pressed }) => [
        styles.card,
        { width: width || '100%' },
        hovered && styles.cardHovered,
        pressed && styles.cardPressed,
      ]}
    >
      <View style={styles.headerRow}>
        <View style={[styles.iconHalo, { backgroundColor: palette.preview }]}>
          <Icon name={template?.icon || 'sparkles'} size={24} color={palette.icon} />
        </View>
        <View style={styles.headerRight}>
          {isNew && (
            <View style={styles.newBadge}>
              <Text size="xs" weight="bold" style={{ color: colors.text_color_secondary }}>
                {t('pages.tasks.newBadge') || 'New'}
              </Text>
            </View>
          )}
          <TaskTemplateFavoriteHeart template={template} />
        </View>
      </View>

      <View style={styles.body}>
        <Text weight="bold" size="m" numberOfLines={2}>
          {template?.name}
        </Text>
        <Text size="s" color="secondary" numberOfLines={2} style={{ lineHeight: 20 }}>
          {template?.description}
        </Text>
      </View>

      <View style={styles.footer}>
        <View style={styles.footerLeft}>
          <Icon name="arrows-rotate" size={11} color={colors.text_color_secondary} />
          <Text size="xs" color="secondary" numberOfLines={1}>
            {scheduleLabel(template)}
          </Text>
        </View>
        <Pressable
          onPress={(e) => {
            e?.stopPropagation?.();
            if (typeof e?.preventDefault === 'function') e.preventDefault();
            scheduleHandler?.(template);
          }}
          accessibilityRole="button"
          accessibilityLabel={t('pages.tasks.scheduleIt') || 'Schedule it'}
          style={({ hovered, pressed }) => [
            styles.cta,
            hovered && styles.ctaHovered,
            pressed && styles.ctaPressed,
          ]}
        >
          <Text size="xs" weight="bold" color="secondary">
            {t('pages.tasks.scheduleIt') || 'Schedule it'}
          </Text>
          <Icon name="arrow-right" size={10} color={colors.text_color_secondary} />
        </Pressable>
      </View>
    </Pressable>
  );
}

export function TemplatePinCardPlaceholder({ width, stretchHeight = false }) {
  const { colors, theme } = useTheme();
  const { isXS, isMD } = useResponsiveness();
  const palette = getCatalogPalette(theme);
  const styles = useMemo(
    () => createStyles(colors, palette, isXS, isMD, stretchHeight),
    [colors, palette, isXS, isMD, stretchHeight],
  );
  return (
    <View style={[styles.card, { width: width || '100%', opacity: 0.6 }]}>
      <View style={styles.headerRow}>
        <View style={[styles.iconHalo, { backgroundColor: colors.solid_color_secondary }]} />
      </View>
      <View style={[styles.body, { gap: 8 }]}>
        <View style={[styles.placeholderBar, { width: '70%', height: 14 }]} />
        <View style={[styles.placeholderBar, { width: '90%', height: 10 }]} />
        <View style={[styles.placeholderBar, { width: '60%', height: 10 }]} />
      </View>
    </View>
  );
}

const createStyles = (colors, palette, isXS, isMD, stretchHeight) => {
  // Compact mode kicks in on tablet (isMD) and below — that's where the
  // body width gets tight enough to start clipping descriptions.
  const compact = isMD;
  const haloSize = 44;
  const haloRadius = compact ? 12 : 14;
  const cardPadding = isXS ? 16 : 18;
  const cardMinHeight = isXS ? 160 : compact ? 180 : 200;

  return StyleSheet.create({
  card: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: palette.border,
    backgroundColor: palette.surface,
    padding: cardPadding,
    gap: compact ? 8 : 10,
    overflow: 'hidden',
    flexDirection: 'column',
    // Native percentage heights resolve against the scroll container, not
    // the wrapped grid row. Grow from content so the footer stays in view.
    ...(stretchHeight
      ? {
          minHeight: cardMinHeight,
          alignSelf: 'stretch',
          ...(Platform.OS === 'web' ? { height: '100%' } : { flexGrow: 1 }),
        }
      : {}),
    ...(Platform.OS === 'web'
      ? { cursor: 'pointer', transition: 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.12s ease' }
      : {}),
  },
  cardHovered: {
    backgroundColor: palette.hover,
    borderColor: palette.activeBorder,
  },
  cardPressed: {
    opacity: 0.96,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  iconHalo: {
    width: haloSize,
    height: haloSize,
    borderRadius: haloRadius,
    alignItems: 'center',
    justifyContent: 'center',
  },
  newBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: palette.preview,
  },
  body: {
    gap: 4,
    ...(stretchHeight ? (Platform.OS === 'web' ? { flex: 1 } : { flexGrow: 1 }) : {}),
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: palette.border,
    cursor: 'pointer',
    ...(Platform.OS === 'web' ? { transition: 'background-color 0.12s ease, border-color 0.12s ease' } : {}),
  },
  ctaHovered: {
    backgroundColor: colors.solid_color_tertiary,
    borderColor: palette.activeBorder,
  },
  ctaPressed: {
    opacity: 0.92,
  },
  placeholderBar: {
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 4,
  },
  });
};

export default React.memo(TemplatePinCard);
